import graphql__facaf590252b734bf806 from "./__generated__/SuppliersViewQuery.graphql.ts";
import graphql__4c2cff081176faa88ba9 from "./__generated__/SuppliersViewAssignCategoryMutation.graphql.ts";
import graphql__b14c5a3b562b34d6c61f from "./__generated__/SuppliersViewAssignBillingCycleMutation.graphql.ts";
import graphql__6ddf21f6d32d374774df from "./__generated__/SuppliersViewRenameMutation.graphql.ts";import * as React from "react";
import {startTransition, useMemo, useState} from "react";
import {useOutletContext} from "react-router";
import {Link} from "react-router-dom";
import {graphql} from "react-relay";
import {usePreloadedQuery, useQueryLoader, useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCodeMerge, faGear, faScissors} from "@fortawesome/pro-regular-svg-icons";
import * as yup from "yup";
import Panel from "./presentational/Panel";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import ActionButton from "./presentational/button/ActionButton";
import ConfigureSupplierDialog from "./dialog/ConfigureSupplierDialog";
import EditableDataListItem from "./form/EditableDataListItem";
import EditableDataListField from "./form/EditableDataListField";
import MergeSupplierDialog from "./dialog/MergeSupplierDialog";
import SeparateSupplierSpellingDialog from "./dialog/SeparateSupplierSpellingDialog";
import {formatAmount} from "../helper/revenueFormat";
import {expenseInvoicesPath} from "../helper/expenseInvoiceFilter";
import {BILLING_CYCLES, BillingCycle, EXPENSE_CATEGORIES, ExpenseCategory} from "../helper/expenseLabels";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../contexts/ErrorContext";
import {startDateForMonthsAgo} from "./AnalysisView";
import SuppliersViewQueryNode from "./__generated__/SuppliersViewQuery.graphql";
import type {SuppliersViewQuery as SuppliersViewQueryType} from "./__generated__/SuppliersViewQuery.graphql";
import type {SuppliersViewAssignCategoryMutation} from "./__generated__/SuppliersViewAssignCategoryMutation.graphql";
import type {SuppliersViewAssignBillingCycleMutation} from "./__generated__/SuppliersViewAssignBillingCycleMutation.graphql";
import type {SuppliersViewRenameMutation} from "./__generated__/SuppliersViewRenameMutation.graphql";

const suppliersViewQuery = graphql__facaf590252b734bf806;

const assignCategoryMutation = graphql__4c2cff081176faa88ba9;

const assignBillingCycleMutation = graphql__b14c5a3b562b34d6c61f;

const renameMutation = graphql__6ddf21f6d32d374774df;

type InvoicesOutletContext = {
    expenseSuppliersQuery: any
};

export type SupplierRow = {
    readonly id: string
    readonly supplierIdentifier: string
    readonly name: string
    readonly expenseCategory: string | null | undefined
    readonly billingCycle: string | null | undefined
    readonly amountEstimateSource: string | null | undefined
    readonly spellings: ReadonlyArray<string>
    readonly netAmountInEuroLastTwelveMonths: number
    readonly invoiceCount: number
    readonly proposedServicePeriodCount: number
};

type OpenDialog =
    | {type: 'configure', supplier: SupplierRow}
    | {type: 'merge', supplier: SupplierRow}
    | {type: 'separate', supplier: SupplierRow, spelling: string}
    | null;

/**
 * Lists all suppliers for assigning expense category and billing cycle. Both are changed right in
 * the row, and so is the name. The rarely changed settings are in the configuration dialog. The order is kept until the list is reloaded, so a row does not jump away once its
 * category is assigned.
 */
const SuppliersView: React.FC = () => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const {expenseSuppliersQuery} = useOutletContext<InvoicesOutletContext>();
    const [queryRef, loadSuppliersQuery] = useQueryLoader<SuppliersViewQueryType>(SuppliersViewQueryNode, expenseSuppliersQuery);
    const data = usePreloadedQuery<SuppliersViewQueryType>(suppliersViewQuery, queryRef ?? expenseSuppliersQuery);
    const [searchTerm, setSearchTerm] = useState('');
    const [openDialog, setOpenDialog] = useState<OpenDialog>(null);

    const suppliers = data.suppliers as ReadonlyArray<SupplierRow>;
    const unassignedCount = suppliers.filter(supplier => !supplier.expenseCategory).length;
    const visibleSuppliers = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (term === '') {
            return suppliers;
        }
        return suppliers.filter(supplier =>
            supplier.name.toLowerCase().includes(term) || supplier.spellings.some(spelling => spelling.toLowerCase().includes(term))
        );
    }, [suppliers, searchTerm]);

    const periodLabel = intl.formatMessage(
        {id: 'view.suppliers.col.netAmount'},
        {
            start: intl.formatDate(startDateForMonthsAgo(11), {year: 'numeric', month: 'short', timeZone: 'UTC'}),
            end: intl.formatDate(startDateForMonthsAgo(0), {year: 'numeric', month: 'short', timeZone: 'UTC'}),
        }
    );

    const reloadSuppliers = () => {
        startTransition(() => {
            loadSuppliersQuery({}, {fetchPolicy: 'network-only'});
        });
    };

    const closeDialog = (changed: boolean) => {
        setOpenDialog(null);
        if (changed) {
            reloadSuppliers();
        }
    };

    const assignCategory = (supplier: SupplierRow, expenseCategory: ExpenseCategory) => {
        commitMutationWithErrorHandling<SuppliersViewAssignCategoryMutation>(
            relayEnvironment,
            {
                mutation: assignCategoryMutation,
                variables: {input: {id: supplier.supplierIdentifier, expenseCategory}},
                optimisticResponse: {assignSupplierExpenseCategory: {id: supplier.id, expenseCategory}},
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToAssignSupplierExpenseCategory'})}
        );
    };

    const assignBillingCycle = (supplier: SupplierRow, billingCycle: BillingCycle) => {
        commitMutationWithErrorHandling<SuppliersViewAssignBillingCycleMutation>(
            relayEnvironment,
            {
                mutation: assignBillingCycleMutation,
                variables: {input: {id: supplier.supplierIdentifier, billingCycle}},
                // The count is only known once the server has proposed the service periods anew.
                optimisticResponse: {assignSupplierBillingCycle: {id: supplier.id, billingCycle, proposedServicePeriodCount: supplier.proposedServicePeriodCount}},
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToAssignSupplierBillingCycle'})}
        );
    };

    const rename = (supplier: SupplierRow, name: string, setEditingActive: (active: boolean) => void) => {
        commitMutationWithErrorHandling<SuppliersViewRenameMutation>(
            relayEnvironment,
            {
                mutation: renameMutation,
                variables: {input: {id: supplier.supplierIdentifier, name}},
                onCompleted: () => setEditingActive(false),
                onError: () => setEditingActive(false),
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToRenameSupplier'})}
        );
    };

    const nameValidationSchema = yup.object().shape({
        name: yup.string()
            .trim()
            .max(255, intl.formatMessage({id: 'validation.field.nameTooLong'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
    });

    // Only a missing expense category is highlighted: assigning it is the actual task here. The
    // billing cycle is optional, without one an invoice counts in its invoice month.
    const categorySelectClassName = (assigned: boolean) =>
        'border rounded pl-2 pr-8 py-1 text-sm w-full ' + (assigned ? 'border-gray-300 text-gray-900' : 'border-amber-400 bg-amber-50 text-gray-500');
    const billingCycleSelectClassName = (assigned: boolean) =>
        'border border-gray-300 rounded pl-2 pr-8 py-1 text-sm w-full ' + (assigned ? 'text-gray-900' : 'text-gray-400');

    return (
        <div className="space-y-6 px-4 sm:px-6 lg:px-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                    {intl.formatMessage({id: 'view.suppliers.summary'}, {total: suppliers.length, unassigned: unassignedCount})}
                </p>
                <input
                    type="search"
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                    placeholder={intl.formatMessage({id: 'view.suppliers.search'})}
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-80 max-w-full"
                />
            </div>

            <Panel fullWidthChildren={true}>
                {suppliers.length === 0 ? (
                    <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.suppliers.empty'})}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader className="pl-4 sm:pl-6 pr-3">{intl.formatMessage({id: 'view.suppliers.col.name'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.suppliers.col.expenseCategory'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.suppliers.col.billingCycle'})}</TableHeader>
                                    <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{periodLabel}</TableHeader>
                                    <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.suppliers.col.invoiceCount'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.suppliers.col.servicePeriods'})}</TableHeader>
                                    <TableHeader className="pl-3 pr-4 sm:pr-6"/>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleSuppliers.map((supplier, index) => (
                                    <tr key={supplier.id} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                        <TableData className="pl-4 sm:pl-6 pr-3">
                                            <EditableDataListItem
                                                fields={(<EditableDataListField type="text" name="name" autoFocus={true} data-1p-ignore="true"/>)}
                                                initialValues={{name: supplier.name}}
                                                validationSchema={nameValidationSchema}
                                                handleSubmission={(values, setEditingActive) => rename(supplier, values.name.trim(), setEditingActive)}
                                            >
                                                <span className="font-medium text-gray-900">{supplier.name}</span>
                                            </EditableDataListItem>
                                            {(supplier.spellings.length > 1 || supplier.spellings[0] !== supplier.name) && (
                                                <ul className="mt-0.5 text-xs text-gray-500">
                                                    {supplier.spellings.map(spelling => (
                                                        <li key={spelling} className="flex items-center gap-1 group/spelling">
                                                            {/* Quotes make leading, trailing and doubled spaces visible */}
                                                            <span className="whitespace-pre">„{spelling}“</span>
                                                            {supplier.spellings.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setOpenDialog({type: 'separate', supplier, spelling})}
                                                                    title={intl.formatMessage({id: 'view.suppliers.action.separate'})}
                                                                    className="invisible group-hover/spelling:visible text-gray-400 hover:text-gray-700"
                                                                >
                                                                    <FontAwesomeIcon icon={faScissors}/>
                                                                </button>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </TableData>
                                        <TableData className="px-3 w-64">
                                            <select
                                                value={supplier.expenseCategory ?? ''}
                                                onChange={event => assignCategory(supplier, event.target.value as ExpenseCategory)}
                                                className={categorySelectClassName(!!supplier.expenseCategory)}
                                            >
                                                {!supplier.expenseCategory && (
                                                    <option value="" disabled>{intl.formatMessage({id: 'view.suppliers.unassigned'})}</option>
                                                )}
                                                {EXPENSE_CATEGORIES.map(category => (
                                                    <option key={category} value={category}>{intl.formatMessage({id: `expense.category.${category}`})}</option>
                                                ))}
                                            </select>
                                        </TableData>
                                        <TableData className="px-3 w-56">
                                            <select
                                                value={supplier.billingCycle ?? ''}
                                                onChange={event => assignBillingCycle(supplier, event.target.value as BillingCycle)}
                                                className={billingCycleSelectClassName(!!supplier.billingCycle)}
                                            >
                                                {!supplier.billingCycle && (
                                                    <option value="" disabled>{intl.formatMessage({id: 'view.suppliers.noBillingCycle'})}</option>
                                                )}
                                                {BILLING_CYCLES.map(billingCycle => (
                                                    <option key={billingCycle} value={billingCycle}>{intl.formatMessage({id: `expense.billingCycle.${billingCycle}`})}</option>
                                                ))}
                                            </select>
                                        </TableData>
                                        <TableData className="px-3 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                            {formatAmount(supplier.netAmountInEuroLastTwelveMonths)}
                                        </TableData>
                                        <TableData className="px-3" textAlignment={TextAlignment.Right}>{supplier.invoiceCount}</TableData>
                                        <TableData className="px-3 whitespace-nowrap">
                                            {supplier.proposedServicePeriodCount > 0 ? (
                                                <Link
                                                    to={expenseInvoicesPath({supplier: supplier.supplierIdentifier, status: 'PROPOSED'})}
                                                    title={intl.formatMessage({id: 'view.suppliers.proposals.title'})}
                                                    className="text-sm text-gray-900 underline decoration-gray-300 hover:decoration-gray-700"
                                                >
                                                    {intl.formatMessage({id: 'view.suppliers.proposals'}, {count: supplier.proposedServicePeriodCount})}
                                                </Link>
                                            ) : (
                                                <span className="text-sm text-gray-300">–</span>
                                            )}
                                        </TableData>
                                        <TableData className="pl-3 pr-4 sm:pr-6 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                            <ActionButton
                                                icon={faGear}
                                                title={intl.formatMessage({id: 'view.suppliers.action.configure'})}
                                                additionalClassname="invisible group-hover:visible"
                                                onClick={() => setOpenDialog({type: 'configure', supplier})}
                                            />
                                            <ActionButton
                                                icon={faCodeMerge}
                                                title={intl.formatMessage({id: 'view.suppliers.action.merge'})}
                                                additionalClassname="invisible group-hover:visible"
                                                onClick={() => setOpenDialog({type: 'merge', supplier})}
                                            />
                                        </TableData>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Panel>

            {openDialog?.type === 'configure' && (
                <ConfigureSupplierDialog supplier={openDialog.supplier} onClose={() => closeDialog(false)}/>
            )}
            {openDialog?.type === 'merge' && (
                <MergeSupplierDialog
                    supplier={openDialog.supplier}
                    otherSuppliers={suppliers.filter(supplier => supplier.id !== openDialog.supplier.id)}
                    onClose={closeDialog}
                />
            )}
            {openDialog?.type === 'separate' && (
                <SeparateSupplierSpellingDialog supplier={openDialog.supplier} spelling={openDialog.spelling} onClose={closeDialog}/>
            )}
        </div>
    );
};

export default SuppliersView;
